import { type Theme } from "@mui/material/styles";

/**
 * Row geometry for the four discrepancy tables, as selectors on the table
 * rather than props on every cell. Follows profileTableSx.ts, including its
 * reason for being its own module: a file that exports both components and
 * constants defeats Vite's Fast Refresh and forces a full reload on every edit.
 *
 * Keyed on data attributes, not `td:first-of-type` as that file can be: the
 * group header rows put a `colSpan` cell in first position with its own
 * horizontal padding, and a positional rule from the table would out-specify
 * it.
 */
/**
 * Width of the Status column in all four tables. Wide enough for the longest
 * chip ("Non-Discrepancy") plus the cell's own 16px right inset.
 */
const STATUS_COLUMN_WIDTH = "150px";

/**
 * Left inset that lines an expanded row's detail up with its row's first text
 * column, so nothing in the detail sits left of "Line 5".
 *
 * The icon cells' widths plus the text cell's own inset: the chevron cell
 * (11 + 28 + 12), the checkbox cell where a table has one (4 + 38 + 12), and
 * the 16px `paddingLeft` the text cell itself carries. That last term is the
 * one to keep in mind: aligning to where the text *cell* begins leaves the
 * detail 16px to its left, because the text does not start at its cell's edge.
 * Measured against the rendered text, not the cell box.
 */
export const EXPANDER_INSET_WITH_SELECTION = "121px";
export const EXPANDER_INSET_NO_SELECTION = "67px";

const ROW_GEOMETRY_SX = {
  /*
   * Centres a row's icon control on its first text line. `1lh` is that line:
   * one line-height of the cell's own text, so nothing here restates a type
   * scale. A taller control overflows the band evenly and does not grow the row.
   *
   * Not `align-items` on the cell: cells are top-aligned per the rule above, so
   * centring on the cell would drop the control into the middle of a wrapped
   * row. Not `vertical-align: baseline` on the cell either, because a ~30px
   * IconButton drags a cell's baseline down and grows every row.
   */
  "& [data-row-icon]": {
    display: "flex",
    alignItems: "center",
    height: "1lh",
  },

  /*
   * A row's chevron must land in the same vertical column as the section and
   * group header chevrons, which are bare icons on the standard 16px inset. The
   * row's chevron is an IconButton, so its own 5px padding would push the glyph
   * to 21px. Insetting the cell by the difference puts the glyph back on 16px
   * while the button keeps its full 30px click target.
   *
   * Horizontal only, and the reason the checkbox padding prop is not used on
   * either icon cell: an icon cell that also sets its vertical padding stops
   * matching the text cells beside it, and its control rides above their first
   * line. Left unset, a small TableCell gives every cell in the row the same 6px.
   */
  '& [data-icon-cell="chevron"]': {
    paddingLeft: "11px",
    paddingRight: "12px",
  },

  /**
   * The same rule at the checkbox column, but tighter on the left. Measured at
   * 16px it left a 28px gap between the chevron and the checkbox (the
   * chevron's 12px right inset plus this), which read as a column break
   * between two controls that belong together.
   */
  '& [data-icon-cell="selection"]': {
    paddingLeft: "4px",
    paddingRight: "12px",
  },

  /*
   * Status is the last column of all four tables, right-aligned so the chips
   * line up down the whole dialog.
   *
   * The four tables have different column counts and different minWidths, and
   * `table-layout: auto` hands the leftover width out differently in each, so
   * a left-aligned Status column starts at a different x per section. Every
   * table is the width of its card and every cell carries the same 16px right
   * inset, so aligning right puts the chips on one line instead.
   *
   * An attribute rather than `:last-child`, for the reason at the top of this
   * file: the expander, group header and empty rows all end in a colSpan cell
   * that is also last, and none of them should be right-aligned.
   */
  "& [data-status-cell]": {
    textAlign: "left",
    /*
     * Left-aligned like every other column, but on a fixed width so the four
     * tables still start Status at the same x.
     *
     * Alignment alone cannot do both. The sections are four separate tables
     * with different column counts, and `table-layout: auto` hands leftover
     * width out differently in each, so a plain left-aligned Status begins in a
     * different place per section. Right-aligning fixed that and was the reason
     * this rule existed, at the cost of being the one column that read against
     * the grain. Fixing the width pins the column instead: it is the last
     * column, so the ones before it absorb the remainder.
     */
    width: STATUS_COLUMN_WIDTH,
    minWidth: STATUS_COLUMN_WIDTH,
  },
} as const;

/**
 * An opaque fill matching the section card, for a sticky cell — a sticky cell
 * has to be opaque or the rows scroll through it.
 *
 * Flat `background.paper` in both modes. The 16% white overlay this carried in
 * dark mode came from when these tables sat straight on the dialog's own
 * elevation-24 Paper; MUI sets `--Paper-overlay` only for `variant="elevation"`
 * (see Paper.js), and the section card is `variant="outlined"`, so it renders
 * flat `paper`. The overlay made the sticky bands read lighter than the card
 * body they sit in. Dark mode already separates the two surfaces by palette:
 * the panel is `canvas` #17191C and the card is `paper` #23272B.
 */
export function opaqueCardFill(theme: Theme) {
  return { backgroundColor: theme.palette.background.paper };
}

/**
 * The whole `sx` for one of the four discrepancy tables: the row geometry above
 * plus the surface treatment — what separates a column header, a field group
 * header and a data row from each other.
 *
 * One function rather than a geometry constant the caller spreads alongside a
 * surface one, because both need a rule on `.MuiTableCell-body` and the second
 * spread would silently replace the first.
 *
 * Every boundary here is a `divider` rule rather than a change of fill. The
 * panes previously stacked three greys within 2 points of each other — the
 * `canvas` panel behind the cards at #F3F3F3, and `grey[100]` #F5F5F5 for the
 * section title band, the group header rows and the row dividers alike — so
 * none of the three read as a boundary. The one fill that remains is the group
 * header row, the only band that never touches the panel.
 *
 * Nothing inside a table is sticky. Only the section title bands pin, one per
 * section, so the dialog always shows which section you are in and never more.
 */
export function discrepancyTableSx(theme: Theme) {
  return {
    ...ROW_GEOMETRY_SX,
    "& .MuiTableHead-root .MuiTableCell-head": {
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    "& .MuiTableBody-root .MuiTableCell-body": {
      // Top-align so multi-line values line up across the row.
      verticalAlign: "top",
      borderBottomColor: theme.palette.divider,
    },
    /*
     * An open row and the detail below it are one block, so the summary row
     * drops its own rule while expanded. Left on, it drew a line across the
     * middle of the block: the row read as closed off from its own values, and
     * the detail below read as the top of whatever came next. The detail cell
     * keeps its rule, which now closes the whole block.
     *
     * `borderBottom` rather than a colour, because the panes set their own
     * `borderBottomColor` per row (selected and previewed rows tint it) and a
     * colour here would only be one more of those to lose to.
     */
    '& .MuiTableBody-root tr[data-expanded="true"] > .MuiTableCell-body': {
      borderBottom: "none",
    },
  };
}

/** The group header row's fill and its hover, the one band that stays a fill. */
export function groupHeaderFill(theme: Theme) {
  const isDark = theme.palette.mode === "dark";
  return {
    // MUI's `grey` palette is mode-independent — flip to darker shades in dark mode.
    bg: isDark ? theme.palette.grey[900] : theme.palette.grey[100],
    hover: isDark ? theme.palette.grey[800] : theme.palette.grey[200],
  };
}
